import { extend, RequestOptionsInit, ResponseError } from 'umi-request'
import debounce from 'lodash/debounce'
import { message as Message } from 'antd'
import { Toast } from 'antd-mobile'
import { addOwlWarn } from '@bike/fe-owl'

import api from './api'

interface Result {
  data: ResponseData
  response: Response
}

const extensions: RequestOptionsInit = {
  method: 'post',
  timeout: 60000,
  getResponse: true,
  requestType: 'json',
  credentials: 'include',
}

// 检查用户代理字符串，判断是否为移动设备
const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
  navigator?.userAgent
)

// 未登录
const unLoginCode = 2001

const debounceMessage = debounce(message => {
  if (isMobile) {
    Toast.clear()
    Toast.show({
      content: message,
      icon: 'fail',
    })
  } else {
    Message.error(message)
  }
}, 400)

const checkLogin = (result: Result) => {
  const { data } = result
  if (data && data.code === unLoginCode && process.env.NODE_ENV !== 'development') {
    debounceMessage(data.message || '用户未登录')
    location.hash = '/login'
  }
  return result
}

const checkYodaLogin = (result: Result) => {
  const { data } = result
  if (data && data.code === unLoginCode && process.env.NODE_ENV !== 'development') {
    console.log('yoda 未登录')
  }
  return result
}

const checkLoginFactory = (options: RequestOptionsInit) => {
  const { yoda } = options
  if (yoda) {
    return checkYodaLogin
  } else {
    return checkLogin
  }
}

const checkSuccess = (result: Result) => {
  const { data } = result
  if (data?.code !== 0 && data?.code !== 200 && data?.code !== unLoginCode) {
    // 参数错误、无操作权限等响应结果
    debounceMessage(data.message)
  }
  return result
}

const dataHandler = (result: Result) => {
  const { data } = result
  if (data && (data.code === 0 || data.code === 200)) {
    return { ...data, code: 0 }
  }
  return { ...data }
}

const errorHandler = (error: ResponseError) => {
  if (error?.response) {
    // 请求已发送但服务端返回状态码非 2xx 的响应
    try {
      addOwlWarn({
        name: `[ajax-res-err] ${error.message}`,
        msg: error,
        config: {
          category: 'ajaxError',
        },
      })
    } catch (e) {
      console.error(e)
    }
  } else {
    try {
      addOwlWarn({
        name: `[ajax-req-err] ${error.message}`,
        msg: error,
        config: {
          category: 'ajaxError',
        },
      })
    } catch (e) {
      console.error(e)
    }
  }
  if (error?.name === 'ResponseError') {
    if (isMobile) {
      Toast.clear()
      Toast.show({
        content: error.message || '网络错误',
        icon: 'fail',
      })
    } else {
      Message.error(error.message || '网络错误')
    }
  }
  return error
}

const request = extend(extensions)

request.interceptors.response.use(async (response, options) => {
  try {
    const { status, ok } = response
    // 捕获响应状态码为2xx的非0响应code
    if (status >= 200 && status < 300) {
      const data = await response.clone().json()
      if (data?.code !== 0 && data?.code !== 200 && data?.code !== unLoginCode) {
        addOwlWarn({
          name: `[ajax-res] ${data.message}`,
          msg: {
            traceid: response.headers?.get('M-Traceid') || '',
            res: data, // 接口返回信息
            config: options, // 接口请求信息
          },
          config: {
            category: 'ajaxError',
          },
        })
      }
    }
  } catch (error) {
    console.error(error)
  }
  return response
})

interface BaseResponseData<T> {
  code?: number
  message?: string
  data?: T
}

interface NewRequestFunction {
  (url: string, options?: RequestOptionsInit): Promise<ResponseData>
  <T extends object>(url: string, options?: RequestOptionsInit): Promise<BaseResponseData<T>>
}

const newRequest: NewRequestFunction = (url: string, options: RequestOptionsInit = {}) => {
  return request(api[url as keyof typeof api], options)
    .then(checkLoginFactory(options))
    .then(checkSuccess)
    .then(dataHandler)
    .catch(error => {
      return errorHandler(error)
    })
}

export default newRequest
